import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IResourceItemType } from '@/api/types';
import type { ICommonItemColumnNames, ISimpleResourceType } from '@/components/common';
import type { IEditResourceModalData } from '@/hooks/types';

import {
  commonItemSortingFunctions,
  getLabel,
  getResourceItemId,
  getResourceItemType,
  getResourceVisibility,
} from '@/helpers';
import { ResourceItemsTable } from '.';
import { useResourceModal, useTableSort } from '@/hooks';
import { ResourceModalType } from '@/hooks/enums';
import { Mobile, TabletAndDesktop } from '@/breakpoints-config/breakpoints';
import { ResourceItemsTableMobile } from './ResourceItemsTableMobile';
import { ResourcesNoData } from '@/components/common';

interface IProps {
  items: IResourceItemType[];
  parentFolderId: number;
  clientId: number;
}

const ResourceItemsContainer = ({ items, parentFolderId, clientId }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const itemsWithStatus = useMemo(
    () =>
      items.map(item => {
        return {
          ...item,
          publicationStatus: getResourceVisibility(item),
        };
      }),
    [items],
  );
  const { sortedData, onSort, setRawData, sortingState } = useTableSort<ICommonItemColumnNames, IResourceItemType>({
    initial: { dateStart: 'DESC' },
    sortingFunctions: commonItemSortingFunctions,
    rawData: itemsWithStatus,
  });
  const { handleOpenModal } = useResourceModal();

  useEffect(() => {
    setRawData(itemsWithStatus);
  }, [itemsWithStatus, setRawData]);

  const openEditModal = useCallback(
    (item: IResourceItemType) =>
      handleOpenModal({
        modalType: ResourceModalType.edit,
        data: {
          parentFolderId,
          clientId,
          resourceId: getResourceItemId(item),
          resourceType: getResourceItemType(item),
          existingResource: item,
        } as IEditResourceModalData,
      }),
    [clientId, handleOpenModal, parentFolderId],
  );

  const openDeleteModal = useCallback(
    (item: IResourceItemType) =>
      handleOpenModal({
        modalType: ResourceModalType.delete,
        data: {
          parentFolderId,
          clientId,
          resourceId: getResourceItemId(item),
          resourceType: getResourceItemType(item) as ISimpleResourceType,
          resourceName: getLabel(item),
        },
      }),
    [clientId, handleOpenModal, parentFolderId],
  );

  if (!items?.length) return <ResourcesNoData label={t`resources.content.noItems`} />;

  return (
    <>
      <TabletAndDesktop>
        <ResourceItemsTable
          items={sortedData}
          sortingState={sortingState}
          onSort={onSort}
          openEditModal={openEditModal}
          openDeleteModal={openDeleteModal}
        />
      </TabletAndDesktop>
      <Mobile>
        <ResourceItemsTableMobile items={sortedData} openEditModal={openEditModal} openDeleteModal={openDeleteModal} />
      </Mobile>
    </>
  );
};

export { ResourceItemsContainer };
